import React, { useContext, useEffect, useRef, useState } from 'react';
import AIEduButton from '../../components/Button/AIEduButton';
import AIEduDataTable from '../../components/DataTable/AIEduDataTable';
import AIEduDialog from '../../components/Dialog/AIEduDialog';
import { InputMask } from 'primereact/inputmask';
import { v4 as uuidv4 } from 'uuid';
import './devices.css';
import { API } from '../../services';
import { useAuthenticate } from '../../hooks';
import greenSuccessCheck from '../../assets/images/green_success_icon.png';
import deviceFailure from '../../assets/images/deviceFailureLarge.svg';
import FileUpload from '../../components/FileUpload/FileUpload';
import { Context } from '../../context/Context';
import { showErrorToast } from '../../services/setup/ApiResponseHandler';
import axiosInstance from '../../services/setup/axiosInstance';
import { downloadCSV } from '../../utils/Utils';
import { useIntl } from 'react-intl';
import { REDIRECT_DURATION } from '../../utils/Constants';

const AddDevice = ({ setIsAddDeviceVisible, setLoading }) => {
    const intl = useIntl();
    const { formatMessage: f } = intl;

    const { accountInfo } = useAuthenticate();
    const { organizations, place } = accountInfo ?? {};
    const { placeId } = place ?? {};
    const { organizationId } = organizations?.[0] ?? {}; // assuming there is only one organization for now. This can be changed later.

    const [visible, setVisible] = useState(false);
    const [boxVisible, setBoxVisible] = useState(false);
    const [devices, setDevices] = useState([{ id: uuidv4(), mac: '' }]);
    const [deviceErrorMessage, setDeviceErrorMessage] = useState('');
    const { triggerRefresh } = useContext(Context);
    const [files, setFiles] = useState([]);
    const [progress, setProgress] = useState(0);
    const [devicesCount, setDevicesCount] = useState(0);
    const [uploadInProgress, setUploadInProgress] = useState(false);
    const [errorUpload, setErrorUpload] = useState(false);
    const [screenMessage, setScreenMessage] = useState('');
    const saveBtnRef = useRef(null);
    const [navigationTimeout, setNavigationTimeout] = useState(null);
    const [differentFileFormatError, setDifferentFileFormatError] =
        useState(false);

    const DeviceAddedPopUp = () => {
        const [counter, setCounter] = useState(10);
        useEffect(() => {
            if (deviceErrorMessage) return;

            let intervalId = null;
            if (navigationTimeout) {
                intervalId = setInterval(() => {
                    setCounter((prevCount) => prevCount - 1);
                    if (counter < 0) {
                        clearInterval(intervalId);
                    }
                }, 1000);
            }

            return () => clearInterval(intervalId);
        }, [navigationTimeout]);

        return (
            <AIEduDialog
                visible={boxVisible}
                draggable={false}
                style={{ width: '40vw', minWidth: '40rem' }}
                onHide={() => {
                    if (!boxVisible) return;
                    setBoxVisible(false);
                }}
                content={
                    <>
                        <div
                            className='flex justify-content-center mt-2 mb-5'
                            style={{ width: '6rem', margin: 'auto' }}
                        >
                            <img
                                src={
                                    deviceErrorMessage
                                        ? deviceFailure
                                        : greenSuccessCheck
                                }
                                style={{
                                    objectFit: 'contain',
                                    height: '100%',
                                    width: '100%',
                                }}
                            />
                        </div>

                        <div
                            className='flex justify-content-center mb-2'
                            style={{
                                fontSize: '24px',
                                fontWeight: '600',
                                textAlign: 'center',
                            }}
                        >
                            {deviceErrorMessage
                                ? deviceErrorMessage
                                : `${devicesCount} devices added successfully`}
                        </div>

                        <div
                            className='flex justify-content-center mb-2'
                            style={{ fontSize: '16px', fontWeight: '500' }}
                        >
                            {deviceErrorMessage && screenMessage}
                            {!deviceErrorMessage &&
                                `Go to device dashboard to view`}
                        </div>

                        <div className='flex justify-content-center mt-6 mb-4'>
                            {!deviceErrorMessage ? (
                                <AIEduButton
                                    label={f({ id: 'AI_EDU_DEVICE_DASHBOARD' })}
                                    className='btnSecondary'
                                    style={{ width: '15rem' }}
                                    onClick={() => {
                                        clearTimeout(navigationTimeout);
                                        setBoxVisible(false);
                                        setDeviceErrorMessage('');
                                        setDevicesCount(0);
                                        navigateToDevice();
                                    }}
                                />
                            ) : (
                                <AIEduButton
                                    label={f({ id: 'AI_EDU_CLOSE' })}
                                    className='btnSecondary'
                                    style={{ width: '15rem' }}
                                    onClick={() => {
                                        setBoxVisible(false);
                                        setScreenMessage('');
                                        setDeviceErrorMessage('');
                                    }}
                                />
                            )}
                        </div>
                        {!deviceErrorMessage && (
                            <div
                                className='flex justify-content-center my-3'
                                style={{ fontFamily: 'SamsungOne-500' }}
                            >
                                Redirecting in {counter}
                            </div>
                        )}
                    </>
                }
            />
        );
    };

    const handleAddDevice = () => {
        const ID = uuidv4();
        setDevices([...devices, { id: ID, mac: '' }]);
    };
    const handleDeleteDevice = (rowData) => {
        setDevices((prev) => prev.filter((device) => device.id !== rowData.id));
    };
    const actionBodyTemplate = (rowData) => {
        return (
            <i
                className='pi pi-trash'
                style={{ fontSize: '20px', cursor: 'pointer' }}
                onClick={() => handleDeleteDevice(rowData)}
            />
        );
    };

    const onMACChange = (e, rowData) => {
        setDevices((prev) =>
            prev.map((device) =>
                device.id === rowData.id
                    ? { ...device, mac: e.target.value }
                    : device
            )
        );
    };

    const inputBodyTemplate = (rowData) => {
        return (
            <InputMask
                id={rowData.id}
                className='addDeviceMacMask'
                value={rowData.mac}
                onChange={(e) => onMACChange(e, rowData)}
                keyfilter='hex'
                mask='** - ** - ** - ** - ** - **'
                placeholder='xx - xx - xx - xx - xx - xx'
            />
        );
    };

    const columns = [
        {
            field: 'mac',
            header: f({ id: 'AI_EDU_DEVICE_MAC' }),
            body: inputBodyTemplate,
        },
        { header: f({ id: 'AI_EDU_ACTION' }), body: actionBodyTemplate },
    ];

    const handleRedirect = () => {
        const timeOut = setTimeout(() => {
            setBoxVisible(false);
            setDeviceErrorMessage('');
            setDevicesCount(0);
            navigateToDevice();
        }, REDIRECT_DURATION);

        setNavigationTimeout(timeOut);
    };

    const validateMACAddressData = (screenIds) => {
        const macIdSet = new Set();
        for (let i = 0; i < screenIds.length; i++) {
            const screen = screenIds[i];
            if (!screen) {
                showErrorToast(f({ id: 'AI_EDU_INVALID_MAC_MSG' }));
                return false;
            }
            if (macIdSet.has(screen)) {
                showErrorToast(f({ id: 'AI_EDU_DUPLICATE_MAC' }));
                return false;
            }
            macIdSet.add(screen);
        }
        return true;
    };

    const handleAddDevicesManually = () => {
        saveBtnRef.current.blur();

        const screenIds = devices?.map((device) => {
            return device.mac?.toLowerCase()?.split(' - ').join('-');
        });

        if (validateMACAddressData(screenIds)) {
            setLoading(true);
            API.addScreens(screenIds, organizationId, placeId)
                .then((res) => {
                    setBoxVisible(true);
                    const { data } = res ?? {};
                    if (res.status !== 201) {
                        const { error, other, same } = data ?? {};
                        const otherScreens = other?.map(
                            ({ screenId }) => screenId
                        );
                        const sameScreens = same?.map(
                            ({ screenId }) => screenId
                        );
                        let message = '';
                        if ([...sameScreens, ...otherScreens]?.length > 0) {
                            message = `Device Ids already present - ${[
                                ...sameScreens,
                                ...otherScreens,
                            ].join(', ')}`;
                            setScreenMessage(message);
                        }
                        setDeviceErrorMessage(error);
                        if (res.status === 500) {
                            setDeviceErrorMessage('Internal Server Error');
                        }
                    } else {
                        const { count } = data ?? {};
                        setDevicesCount(count);
                        setDeviceErrorMessage('');
                        handleRedirect();
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const closeFileUpload = () => (
        <div className='flex justify-content-end mr-4'>
            <i
                style={{ cursor: 'pointer' }}
                className='pi pi-times'
                onClick={resetFileUpload}
            />
        </div>
    );

    const handleFileUpload = () => {
        const formData = new FormData();
        formData.append('files', files[0]);
        setUploadInProgress(true);
        axiosInstance
            .post(`/ums/${organizationId}/${placeId}/screens`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (event) => {
                    const percent = Math.round(
                        (event.loaded * 100) / event.total
                    );
                    setProgress(percent);
                },
            })
            .then(({ data }) => {
                setVisible(false);
                setBoxVisible(true);
                handleRedirect();

                const { count } = data ?? {};
                setDevicesCount(count);
                setDeviceErrorMessage('');
            })
            .catch(({ response }) => {
                const { data, status } = response ?? {};
                const { error } = data ?? {};
                setErrorUpload(true);
                if (status === 500) {
                    showErrorToast('Internal Server Error');
                } else {
                    // showErrorToast(error);
                    setBoxVisible(false);
                    setDeviceErrorMessage(error);
                }
            })
            .finally(() => {
                setUploadInProgress(false);
            });
    };

    const resetFileUpload = () => {
        setVisible(false);
        setFiles([]);
        setProgress(0);
        setErrorUpload(false);
        setUploadInProgress(false);
        setDifferentFileFormatError('');
    };

    const navigateToDevice = () => {
        setIsAddDeviceVisible(false);
        triggerRefresh('device');
    };

    return (
        <div className='devices add-devices'>
            <div className='add_device_header'>
                <span
                    onClick={navigateToDevice}
                    className='cursor-pointer hover:underline'
                >
                    {f({ id: 'AI_EDU_DEVICES' })}
                </span>
                <i className='pi pi-chevron-right mx-2' />
                <span>Add Device</span>
            </div>
            <div className='mt-2 mb-3 add_device_text'>
                {f({ id: 'AI_EDU_BULK_DEVICE_ADDITION' })}
            </div>
            <div className='card flex justify-content-start'>
                <div className='upload_file'>
                    <AIEduButton
                        label={f({ id: 'AI_EDU_UPLOAD_FILE' })}
                        icon='pi pi-upload'
                        className='mr-6 btnPrimary'
                        style={{ width: '12rem' }}
                        onClick={() => setVisible(true)}
                    />
                    <div className='supported_text'>{`(Supported format: .csv | Max rows: 1000 | Max size: 300kb)`}</div>
                </div>
                <AIEduDialog
                    title={closeFileUpload}
                    visible={visible}
                    draggable={false}
                    onHide={() => {
                        if (!visible) return;
                        resetFileUpload();
                    }}
                    style={{ width: '50vw' }}
                    content={
                        <FileUpload
                            text={f({ id: 'AI_EDU_ADD_ALL_DEVICES' })}
                            handleFileUpload={handleFileUpload}
                            files={files}
                            setFiles={setFiles}
                            progress={progress}
                            setProgress={setProgress}
                            errorUpload={errorUpload}
                            setUploadInProgress={setUploadInProgress}
                            uploadInProgress={uploadInProgress}
                            setErrorUpload={setErrorUpload}
                            errorMessage={deviceErrorMessage}
                            differentFileFormatError={differentFileFormatError}
                            setDifferentFileFormatError={
                                setDifferentFileFormatError
                            }
                        />
                    }
                />
                <div
                    className='flex download_file_text'
                    style={{ lineHeight: '40px' }}
                    onClick={() =>
                        downloadCSV(
                            [
                                ['Mac Address'],
                                ['53-58-55-53-58-55'],
                                ['53-58-55-53-58-56'],
                            ],
                            'device_format.csv'
                        )
                    }
                >
                    <div className='mr-2'>
                        <i className='pi pi-download' />
                    </div>
                    <div>{f({ id: 'AI_EDU_DOWNLOAD_FILE_FORMAT' })}</div>
                </div>
            </div>

            <div
                className='flex justify-content-between mt-3 mb-2'
                style={{ width: '100%' }}
            >
                <div style={{ fontFamily: 'SamsungOne-400, sans-serif' }}>
                    OR
                </div>
                <div className='text_center_line' />
            </div>

            <div className='mt-3 mb-3 add_device_text'>
                {f({ id: 'AI_EDU_ADD_DEVICES_MANUALLY' })}
            </div>
            <AIEduDataTable
                data={devices}
                columns={columns}
                className='add_device_table'
                scrollHeight='45vh'
            />
            <div className='flex mt-4'>
                <div className='add_more_device-text' onClick={handleAddDevice}>
                    <i
                        className='pi pi-plus mr-2'
                        style={{ fontSize: '16px' }}
                    />
                    {f({ id: 'AI_EDU_ADD_MORE' })}
                </div>
            </div>
            <AIEduButton
                label={f({ id: 'AI_EDU_SAVE' })}
                ref={saveBtnRef}
                className='mt-4 mr-6 btnPrimary'
                onClick={handleAddDevicesManually}
                disabled={devices.length === 0}
            />
            <DeviceAddedPopUp />
        </div>
    );
};

export default AddDevice;
